import React, { useState, useEffect, useCallback } from 'react';
import "../style.css";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function Multimedia() {
    const [mediaItems, setMediaItems] = useState([]);
    const [filteredMediaItems, setFilteredMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [filterOpen, setFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const resultsPerPage = 8;
    const navigate = useNavigate();

    const [pendingFilters, setPendingFilters] = useState({
        state: "",
        category: "",
        disciplines: "",
        rockType: "",
        majorLithology: "",
        formation: "",
        age: ""
    });

    const toggleFilter = () => {
        setFilterOpen(!filterOpen);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get('https://geoid-rest.vercel.app/media');
                const publicMedia = response.data.filter(media => media.access === 'public');
                setMediaItems(publicMedia);
                setFilteredMediaItems(publicMedia);
            } catch (error) {
                console.error('Error fetching multimedia data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const applyFilters = useCallback(() => {
        let filtered = mediaItems;

        // Apply search keyword filter
        if (searchKeyword) {
            filtered = filtered.filter(item =>
                item.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                item.location?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
                item.category?.toLowerCase().includes(searchKeyword.toLowerCase())
            );
        }

        // Apply dropdown filters
        filtered = filtered.filter(item => {
            return (
                (!pendingFilters.state || item.state === pendingFilters.state) &&
                (!pendingFilters.category || item.category === pendingFilters.category) &&
                (!pendingFilters.disciplines || item.disciplines === pendingFilters.disciplines) &&
                (!pendingFilters.rockType || item.rockType === pendingFilters.rockType) &&
                (!pendingFilters.majorLithology || item.majorLithology === pendingFilters.majorLithology) &&
                (!pendingFilters.formation || item.formation === pendingFilters.formation) &&
                (!pendingFilters.age || item.age === pendingFilters.age)
            );
        });

        setFilteredMediaItems(filtered);
        setCurrentPage(1);
    }, [mediaItems, searchKeyword, pendingFilters]);

    const handleSearch = (e) => {
        const keyword = e.target.value.toLowerCase();
        setSearchKeyword(keyword);

        let filtered = mediaItems;
        if (keyword.trim() !== '') {
            filtered = mediaItems.filter(item => {
                return (
                    item.name?.toLowerCase().includes(keyword) ||
                    item.location?.toLowerCase().includes(keyword) ||
                    item.category?.toLowerCase().includes(keyword) ||
                    item.state?.toLowerCase().includes(keyword) ||
                    item.disciplines?.toLowerCase().includes(keyword) ||
                    item.rockType?.toLowerCase().includes(keyword) ||
                    item.majorLithology?.toLowerCase().includes(keyword) ||
                    item.formation?.toLowerCase().includes(keyword) ||
                    item.age?.toLowerCase().includes(keyword)
                );
            });
        }

        // Apply any active filters to the search results
        filtered = filtered.filter(item => {
            return (
                (!pendingFilters.state || item.state === pendingFilters.state) &&
                (!pendingFilters.category || item.category === pendingFilters.category) &&
                (!pendingFilters.disciplines || item.disciplines === pendingFilters.disciplines) &&
                (!pendingFilters.rockType || item.rockType === pendingFilters.rockType) &&
                (!pendingFilters.majorLithology || item.majorLithology === pendingFilters.majorLithology) &&
                (!pendingFilters.formation || item.formation === pendingFilters.formation) &&
                (!pendingFilters.age || item.age === pendingFilters.age)
            );
        });

        setFilteredMediaItems(filtered);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setPendingFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResetFilters = () => {
        setPendingFilters({
            state: "",
            category: "",
            disciplines: "",
            rockType: "",
            majorLithology: "",
            formation: "",
            age: ""
        });
        setSearchKeyword("");
        setFilteredMediaItems(mediaItems);
        setCurrentPage(1);
    };

    const openMediaViewer = useCallback((mediaId) => {
        navigate(`/viewMedia/${mediaId}`);
    }, [navigate]);

    // Pagination logic
    const indexOfLastMedia = currentPage * resultsPerPage;
    const indexOfFirstMedia = indexOfLastMedia - resultsPerPage;
    const currentMediaItems = filteredMediaItems.slice(indexOfFirstMedia, indexOfLastMedia);
    const totalPages = Math.ceil(filteredMediaItems.length / resultsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <p>Loading data...</p>
            </div>
        );
    }

    return (
        <div>
            <section className="hero-media">
                <div>
                    <h1>Digging is discovering forgotten lives</h1>
                    <p>Explore all of our captured media</p>
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search name, location, category..."
                            className="search-bar-page"
                            value={searchKeyword}
                            onChange={handleSearch}
                        />
                    </div>
                </div>
            </section>
            <div className="model-page">
                <div className='filterMenuMobile'>
                    <div onClick={toggleFilter}>
                        Filter
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#00712D" className="bi bi-caret-down-fill" viewBox="0 0 16 16">
                            <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                        </svg>
                    </div>
                    {filterOpen && (
                        <div className='filterMenuBox'>
                            {/* Mobile filter content */}
                        </div>
                    )}
                </div>
                <div className='filterMenuBox'>
                    <p style={{textAlign: 'left'}}>State</p>
                    <label>
                        <select
                            name="state"
                            className="dropdown"
                            value={pendingFilters.state}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">State</option>
                            <option value="Terengganu">Terengganu</option>
                            <option value="Kedah">Kedah</option>
                            <option value="Johor">Johor</option>
                            <option value="Kelantan">Kelantan</option>
                            <option value="Malacca">Malacca</option>
                            <option value="Negeri Sembilan">Negeri Sembilan</option>
                            <option value="Pahang">Pahang</option>
                            <option value="Penang">Penang</option>
                            <option value="Perak">Perak</option>
                            <option value="Perlis">Perlis</option>
                            <option value="Sabah">Sabah</option>
                            <option value="Sarawak">Sarawak</option>
                            <option value="Selangor">Selangor</option>
                            <option value="Kuala Lumpur">Kuala Lumpur</option>
                            <option value="Labuan">Labuan</option>
                            <option value="Putrajaya">Putrajaya</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Category</p>
                    <label>
                        <select
                            name="category"
                            className="dropdown"
                            value={pendingFilters.category}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Category</option>
                            <option value="Geology">Geology</option>
                            <option value="Archaeology">Archaeology</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Disciplines</p>
                    <label>
                        <select
                            name="disciplines"
                            className="dropdown"
                            value={pendingFilters.disciplines}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Disciplines</option>
                            <option value="Geology - all">Geology - all</option>
                            <option value="Geology - general">Geology - general</option>
                            <option value="Geology - oil & gas">Geology - oil & gas</option>
                            <option value="Geology - mining">Geology - mining</option>
                            <option value="Geology - engineering">Geology - engineering/geotechnical</option>
                            <option value="Geology - environment">Geology - environment</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Type of Rocks</p>
                    <label>
                        <select
                            name="rockType"
                            className="dropdown"
                            value={pendingFilters.rockType}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Type of Rock</option>
                            <option value="Sedimentary">Sedimentary Rock</option>
                            <option value="Igneous">Igneous Rock</option>
                            <option value="Metamorphic">Metamorphic Rock</option>
                            <option value="Mineral">Mineral</option>
                            <option value="Fossil">Fossil</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Major Lithology</p>
                    <label>
                        <select
                            name="majorLithology"
                            className="dropdown"
                            value={pendingFilters.majorLithology}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Major Lithology</option>
                            <option value="Chert">Chert</option>
                            <option value="Phyllite">Phyllite & quartzite metasediment</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Formation</p>
                    <label>
                        <select
                            name="formation"
                            className="dropdown"
                            value={pendingFilters.formation}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Formation</option>
                            <option value="Semanggol">Semanggol Formation</option>
                            <option value="Labang">Labang Formation</option>
                            <option value="Kuantan">Kuantan Group</option>
                        </select>
                    </label>
                    <p style={{textAlign: 'left'}}>Age</p>
                    <label>
                        <select
                            name="age"
                            className="dropdown"
                            value={pendingFilters.age}
                            onChange={handleFilterChange}
                            style={{width: '100%'}}
                        >
                            <option value="">Age</option>
                            <option value="Triassic">Triassic</option>
                            <option value="Early Permian - Middle Triassic">Early Permian - Middle Triassic</option>
                            <option value="Oligocene - Lower Miocene">Oligocene - Lower Miocene</option>
                            <option value="Carboniferous">Carboniferous</option>
                            <option value="4th-11th Century">4th-11th Century</option>
                        </select>
                    </label>
                    <button className='reset-btn-model' onClick={handleResetFilters}>Reset Filter</button>
                    <button className='apply-btn-model' onClick={applyFilters}>Apply Filter</button>
                </div>
                <div className="artifacts-grid-model">
                    {currentMediaItems.map((item) => (
                        <div className="artifact-card" key={item._id} onClick={() => openMediaViewer(item._id)}>
                            {item.type === "image" ? (
                                <img src={item.imgSrc} alt={item.name} className="artifact-image" />
                            ) : (
                                <video className="artifact-image" src={item.imgSrc} alt={item.name} />
                            )}
                            <div className="artifact-info">
                                <h3>{item.name.length > 20 ? `${item.name.slice(0, 20)}...` : item.name}</h3>
                                <p>{item.location}</p>
                            </div>
                            <div>
                                <button className="view-button-model">
                                    View Media {'>'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="pagination-controls">
                <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                    Previous
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                    <button
                        key={index + 1}
                        onClick={() => handlePageClick(index + 1)}
                        className={currentPage === index + 1 ? 'active' : ''}
                    >
                        {index + 1}
                    </button>
                ))}
                <button onClick={handleNextPage} disabled={currentPage >= totalPages}>
                    Next
                </button>
            </div>
        </div>
    );
}

export default Multimedia;